// A readable device name from a User-Agent string: browser and system only, simple patterns, no library.
// Good enough to tell "my laptop" from "my phone"; never used for anything security relevant.

export type DeviceKind = 'phone' | 'tablet' | 'desktop';
export interface DeviceInfo { kind: DeviceKind; browser: string | null; os: string | null }

/** First match wins: the order matters (Edge and Opera also say "Chrome", Chrome also says "Safari"). */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg(e|A|iOS)?\//, 'Edge'],
  [/\b(OPR|Opera)\//, 'Opera'],
  [/\bSamsungBrowser\//, 'Samsung Internet'],
  [/\b(Firefox|FxiOS)\//, 'Firefox'],
  [/(Chrome|CriOS|Chromium)\//, 'Chrome'], // also HeadlessChrome
  [/\bVersion\/[\d.]+.*\bSafari\//, 'Safari'],
  [/\bcurl\//, 'curl'],
];

const SYSTEMS: [RegExp, string][] = [
  [/\biPhone\b/, 'iPhone'],
  [/\biPad\b/, 'iPad'],
  [/\bAndroid\b/, 'Android'],
  [/\bWindows\b/, 'Windows'],
  [/\bCrOS\b/, 'ChromeOS'],
  [/\b(Macintosh|Mac OS X)\b/, 'macOS'],
  [/\bLinux\b/, 'Linux'],
];

const first = (ua: string, list: [RegExp, string][]) => list.find(([re]) => re.test(ua))?.[1] ?? null;

export function parseUserAgent(ua: string | null | undefined): DeviceInfo {
  const s = ua ?? '';
  const os = first(s, SYSTEMS);
  const kind: DeviceKind =
    os === 'iPhone' || (os === 'Android' && /\bMobile\b/.test(s)) ? 'phone' : os === 'iPad' || os === 'Android' || /\bTablet\b/.test(s) ? 'tablet' : 'desktop';
  return { kind, browser: first(s, BROWSERS), os };
}

/** "Chrome · Windows", "Safari · iPhone", or null when neither is recognised. */
export function deviceLabel(info: DeviceInfo): string | null {
  const parts = [info.browser, info.os].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
